import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { fetchConversations, fetchMessages, sendMessage } from '../redux/messageSlice';
import '../styles/Messages.css';

const Messages = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { conversations, messages } = useSelector(state => state.messages);
  const { user } = useSelector(state => state.auth);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [messageText, setMessageText] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showSearch, setShowSearch] = React.useState(false);

  useEffect(() => {
    dispatch(fetchConversations());
  }, [dispatch]);

  useEffect(() => {
    if (selectedUser) {
      dispatch(fetchMessages(selectedUser._id));
    }
  }, [selectedUser, dispatch]);

  const handleSearch = async (value) => {
    setSearchTerm(value);
    if (value.trim()) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/search?username=${value}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSearchResults(response.data);
      } catch (err) {
        console.error('Error searching users:', err);
      }
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setShowSearch(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (messageText.trim() && selectedUser) {
      dispatch(sendMessage({ receiverId: selectedUser._id, content: messageText }));
      setMessageText('');
    }
  };

  return (
    <div className="messages-container">
      <div className="messages-sidebar">
        <h2>{t('messages.conversations')}</h2>
        
        <button 
          onClick={() => setShowSearch(!showSearch)}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: '#1dd1a1',
            color: 'black',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginBottom: '1rem'
          }}
        >
          ➕ New Message
        </button>

        {showSearch && (
          <div style={{marginBottom: '1rem'}}>
            <input
              type="text"
              placeholder={ 'Search users...'}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: '#252525',
                border: '1px solid #2a2a2a',
                borderRadius: '8px',
                color: 'white',
                marginBottom: '0.5rem'
              }}
            />
            <div style={{maxHeight: '200px', overflowY: 'auto'}}>
              {searchResults.map(user => (
                <div
                  key={user._id}
                  onClick={() => handleSelectUser(user)}
                  style={{
                    padding: '0.75rem',
                    background: '#1a1a1a',
                    border: '1px solid #2a2a2a',
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1dd1a1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#2a2a2a';
                  }}
                >
                  <div style={{fontWeight: 'bold', color: 'white'}}>{user.firstName} {user.lastName}</div>
                  <div style={{color: '#999', fontSize: '0.85rem'}}>@{user.username}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="conversations-list">
          {conversations.map(conv => (
            <div
              key={conv._id}
              className={`conversation-item ${selectedUser?._id === conv._id ? 'active' : ''}`}
              onClick={() => handleSelectUser(conv)}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                {conv.icon && (
                  <img 
                    src={`http://localhost:5000${conv.icon}`} 
                    alt="icon"
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                      backgroundColor: 'transparent'
                    }}
                  />
                )}
                <div>
                  <h4 style={{margin: 0, display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
                    <span style={{color: conv.nicknameColor || '#ffffff'}}>{conv.firstName} {conv.lastName}</span>
                    {conv.badge && <span style={{fontSize: '0.8rem'}}>{conv.badge}</span>}
                  </h4>
                  <p>@{conv.username}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="messages-main">
        {selectedUser ? (
          <>
            <div className="messages-header">
              <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                {selectedUser.icon && (
                  <img 
                    src={`http://localhost:5000${selectedUser.icon}`} 
                    alt="icon"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      backgroundColor: 'transparent'
                    }}
                  />
                )}
                <div>
                  <h2 style={{margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                    <span style={{color: selectedUser.nicknameColor || '#ffffff'}}>{selectedUser.firstName} {selectedUser.lastName}</span>
                    {selectedUser.badge && <span>{selectedUser.badge}</span>}
                  </h2>
                  <p style={{margin: 0, color: '#999', fontSize: '0.9rem'}}>@{selectedUser.username}</p>
                </div>
              </div>
            </div>

            <div className="messages-list">
              {messages.map(msg => (
                <div
                  key={msg._id}
                  className={`message ${msg.sender._id === user?._id ? 'sent' : 'received'}`}
                >
                  <p>{msg.content}</p>
                  <span className="timestamp">
                    {new Date(msg.createdAt).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>

            <form className="message-form" onSubmit={handleSendMessage}>
              <input
                type="text"
                placeholder={t('messages.typeMessage')}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
              <button type="submit" disabled={!messageText.trim()}>{t('messages.send')}</button>
            </form>
          </>
        ) : (
          <div className="no-selection">
            <p>{t('messages.noConversations')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;
